import { tokens } from '@/lib/tokens.js';
import { useState } from 'react';
import { useAdminRole } from '../../hooks/use-admin-roles.js';
import { MembersTab } from './members-tab.js';
import { RulesTab } from './rules-tab.js';

type Tab = 'info' | 'members' | 'rules';

const s = {
  container: {
    backgroundColor: tokens.colors.bgCardDark,
    borderRadius: tokens.radius.card,
    boxShadow: '0 4px 6px -1px rgba(0,0,0,.1)',
    overflow: 'hidden',
  },
  header: {
    padding: '1rem 1.5rem',
    borderBottom: `1px solid ${tokens.colors.borderDark}`,
  },
  title: { fontSize: '1.125rem', fontWeight: 700, color: tokens.colors.textDark, margin: 0 },
  tabs: {
    display: 'flex',
    borderBottom: `1px solid ${tokens.colors.borderDark}`,
    backgroundColor: '#0f172a',
  },
  tab: (active: boolean) => ({
    padding: '0.625rem 1.25rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    color: active ? '#fff' : tokens.colors.textMutedDark,
    borderBottom: active ? `2px solid ${tokens.colors.primary}` : '2px solid transparent',
    fontWeight: active ? 600 : 400,
    transition: 'all 0.2s',
  }),
  body: { padding: '1.5rem' },
  metaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  metaItem: { display: 'flex', flexDirection: 'column' as const, gap: '0.25rem' },
  metaLabel: {
    fontSize: '0.75rem',
    color: tokens.colors.textMutedDark,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  metaValue: { fontSize: '0.875rem', color: tokens.colors.textDark },
};

interface RoleDetailProps {
  tenantId: string;
  roleId: string;
  onEditClick?: () => void;
}

export function RoleDetail({ tenantId, roleId, onEditClick }: RoleDetailProps) {
  const [tab, setTab] = useState<Tab>('info');
  const { data, isLoading, isError } = useAdminRole(tenantId, roleId);

  if (isLoading) {
    return (
      <output
        style={{ display: 'block', padding: '2rem', color: tokens.colors.textMutedDark }}
        aria-label="Loading role"
      >
        Loading…
      </output>
    );
  }

  if (isError || !data) {
    return (
      <div role="alert" style={{ color: tokens.colors.error, padding: '1rem' }}>
        Error loading role
      </div>
    );
  }

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={s.title}>{data.name}</h2>
            <span
              style={{
                fontSize: '0.875rem',
                color: tokens.colors.textMutedDark,
                fontFamily: 'monospace',
              }}
            >
              {data.code}
            </span>
          </div>
          <button
            type="button"
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${tokens.colors.borderDark}`,
              borderRadius: tokens.radius.button,
              color: tokens.colors.textDark,
              padding: '0.375rem 0.875rem',
              fontSize: '0.875rem',
              cursor: 'pointer',
            }}
            onClick={onEditClick}
            aria-label="Edit role"
          >
            Edit
          </button>
        </div>
      </div>

      <div style={s.tabs} role="tablist">
        {(['info', 'members', 'rules'] as Tab[]).map((t) => (
          <button
            type="button"
            key={t}
            style={s.tab(tab === t)}
            onClick={() => setTab(t)}
            role="tab"
            aria-selected={tab === t}
            aria-controls={`tab-${t}`}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
            {t === 'members' && data.stats && ` (${data.stats.members_count})`}
            {t === 'rules' && data.stats && ` (${data.stats.rules_count})`}
          </button>
        ))}
      </div>

      <div style={s.body} id={`tab-${tab}`} role="tabpanel">
        {tab === 'info' && (
          <div style={s.metaGrid}>
            {data.description && (
              <div style={{ ...s.metaItem, gridColumn: '1 / -1' }}>
                <span style={s.metaLabel}>Description</span>
                <span style={s.metaValue}>{data.description}</span>
              </div>
            )}
            <div style={s.metaItem}>
              <span style={s.metaLabel}>Default</span>
              <span style={s.metaValue}>{data.is_default ? 'Yes' : 'No'}</span>
            </div>
            <div style={s.metaItem}>
              <span style={s.metaLabel}>Created</span>
              <span style={s.metaValue}>{new Date(data.created_at).toLocaleString()}</span>
            </div>
            {data.created_by && (
              <div style={s.metaItem}>
                <span style={s.metaLabel}>Created by</span>
                <span style={s.metaValue}>{data.created_by}</span>
              </div>
            )}
            {data.stats && (
              <>
                <div style={s.metaItem}>
                  <span style={s.metaLabel}>Members</span>
                  <span style={s.metaValue}>{data.stats.members_count}</span>
                </div>
                <div style={s.metaItem}>
                  <span style={s.metaLabel}>Grants</span>
                  <span style={s.metaValue}>{data.stats.grants_count}</span>
                </div>
                <div style={s.metaItem}>
                  <span style={s.metaLabel}>Rules</span>
                  <span style={s.metaValue}>{data.stats.rules_count}</span>
                </div>
              </>
            )}
          </div>
        )}

        {tab === 'members' && <MembersTab tenantId={tenantId} roleId={roleId} />}
        {tab === 'rules' && <RulesTab tenantId={tenantId} roleId={roleId} />}
      </div>
    </div>
  );
}
